import { supabase } from "@/integrations/supabase/client";
import { MealSuggestionServiceResponse, DbMealSuggestion } from "@/types/mealSuggestion";

export const mealSuggestionService = {
  /**
   * Generate new meal suggestions using AI
   */
  generateSuggestions: async (): Promise<MealSuggestionServiceResponse> => {
    try {
      const { data, error } = await supabase.functions.invoke('suggest-meals');

      if (error) {
        console.error('Error generating meal suggestions:', error);
        return {
          success: false,
          message: error.message || 'Failed to generate meal suggestions'
        };
      }

      if (!data.success) {
        return {
          success: false,
          message: data.error || 'Failed to generate meal suggestions'
        };
      }

      return {
        success: true,
        data: data.data
      };
    } catch (error) {
      console.error('Error in generateSuggestions:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  },

  /**
   * Get the latest meal suggestions for the user's family
   */
  getLatestSuggestions: async (): Promise<MealSuggestionServiceResponse> => {
    try {
      const { data, error } = await supabase
        .from('meal_suggestions')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error fetching meal suggestions:', error);
        return {
          success: false,
          message: error.message
        };
      }

      return {
        success: true,
        data: data ? {
          ...data,
          suggestions: data.suggestions as any,
          input_data: data.input_data as any,
          openai_response: data.openai_response as any
        } as DbMealSuggestion : null
      };
    } catch (error) {
      console.error('Error in getLatestSuggestions:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  },

  /**
   * Search for recipe details using web scraping
   */
  searchRecipeDetails: async (searchTerms: string): Promise<{ success: boolean; data?: any; message?: string }> => {
    try {
      console.log('🔍 Searching for recipe with terms:', searchTerms);
      
      if (!searchTerms || searchTerms.trim() === '') {
        console.error('❌ Search terms are empty or invalid');
        return { success: false, message: 'Search terms are required' };
      }

      const trimmedTerms = searchTerms.trim();
      console.log('🔍 Using search terms:', trimmedTerms);

      // Use a more specific search URL format that the scraper can handle better
      const searchUrl = `https://www.allrecipes.com/search/results/?search=${encodeURIComponent(trimmedTerms)}`;
      console.log('🌐 Search URL:', searchUrl);
      
      const { data, error } = await supabase.functions.invoke('scrape-recipe', {
        body: { url: searchUrl }
      });

      console.log('📡 Scrape-recipe response:', { data: !!data, error: !!error });

      if (error) {
        console.error('❌ Edge function error:', error);
        return { 
          success: false, 
          message: `Failed to search for recipe: ${error.message || 'Edge Function returned a non-2xx status code'}` 
        };
      }

      if (!data) {
        console.error('❌ No data returned from scrape function');
        return { 
          success: false, 
          message: 'No response from recipe search service' 
        };
      }

      if (!data.title) {
        console.error('❌ Recipe data missing title:', data);
        return { 
          success: false, 
          message: 'No recipe found with those search terms. Try different keywords.' 
        };
      }

      console.log('✅ Recipe found:', data.title);
      return { success: true, data };
    } catch (error) {
      console.error('❌ Error in searchRecipeDetails:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to search for recipe - please try again'
      };
    }
  },

  /**
   * Accept a meal suggestion and add it to meal plans
   */
  acceptSuggestion: async (
    suggestion: any,
    mealType: string = 'dinner'
  ): Promise<{ success: boolean; message?: string }> => {
    try {
      // Get user's current family ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { success: false, message: 'User not authenticated' };
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('current_family_id')
        .eq('id', user.id)
        .single();

      if (!profile?.current_family_id) {
        return { success: false, message: 'No family found' };
      }

      // Validate and format date
      const targetDate = suggestion.targetDate;
      if (!targetDate) {
        return { success: false, message: 'Invalid suggestion: missing target date' };
      }

      // Ensure date is in correct format
      const dateObj = new Date(targetDate);
      if (isNaN(dateObj.getTime())) {
        return { success: false, message: 'Invalid date format in suggestion' };
      }
      const formattedDate = dateObj.toISOString().split('T')[0];

      if (suggestion.isFromCollection && suggestion.recipeId) {
        // Add existing recipe to meal plan
        const { error } = await supabase
          .from('meal_plans')
          .insert({
            date: formattedDate,
            recipe_id: suggestion.recipeId,
            meal_type: mealType || suggestion.mealType || 'dinner',
            family_id: profile.current_family_id,
            user_id: user.id
          });

        if (error) {
          console.error('Error adding recipe to meal plan:', error);
          return { success: false, message: error.message };
        }

        return { success: true, message: 'Recipe added to meal plan!' };
      } else {
        // For new recipes, create a placeholder - user should preview first
        return { success: false, message: 'Please preview the recipe details first before accepting' };
      }
    } catch (error) {
      console.error('Error in acceptSuggestion:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  },

  /**
   * Accept a new recipe suggestion with full recipe details
   */
  acceptNewRecipe: async (
    suggestion: any,
    recipeDetails: any,
    mealType: string = 'dinner'
  ): Promise<{ success: boolean; message?: string }> => {
    try {
      // Get user's current family ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { success: false, message: 'User not authenticated' };
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('current_family_id')
        .eq('id', user.id)
        .single();

      if (!profile?.current_family_id) {
        return { success: false, message: 'No family found' };
      }

      // Create new recipe with scraped details
      const newRecipe = {
        title: recipeDetails.title || suggestion.recipeName,
        description: recipeDetails.description || suggestion.newRecipeDetails?.description || '',
        ingredients: recipeDetails.ingredients || ['Add ingredients here'],
        instructions: recipeDetails.instructions || ['Add instructions here'],
        prep_time: recipeDetails.prep_time || suggestion.newRecipeDetails?.estimatedPrepTime,
        cook_time: recipeDetails.cook_time,
        total_time: recipeDetails.total_time,
        servings: recipeDetails.servings,
        source_url: recipeDetails.source_url,
        image_url: recipeDetails.image_url,
        storage_image_url: recipeDetails.storage_image_url,
        storage_image_path: recipeDetails.storage_image_path,
        tags: recipeDetails.tags || suggestion.newRecipeDetails?.tags || [],
        user_id: user.id
      };

      const { data: createdRecipe, error: recipeError } = await supabase
        .from('recipes')
        .insert(newRecipe)
        .select()
        .single();

      if (recipeError) {
        console.error('Error creating new recipe:', recipeError);
        return { success: false, message: recipeError.message };
      }

      // Validate and format date
      const targetDate = suggestion.targetDate;
      if (!targetDate) {
        return { success: false, message: 'Invalid suggestion: missing target date' };
      }

      const dateObj = new Date(targetDate);
      if (isNaN(dateObj.getTime())) {
        return { success: false, message: 'Invalid date format in suggestion' };
      }
      const formattedDate = dateObj.toISOString().split('T')[0];

      // Add new recipe to meal plan
      const { error: mealPlanError } = await supabase
        .from('meal_plans')
        .insert({
          date: formattedDate,
          recipe_id: createdRecipe.id,
          meal_type: mealType || suggestion.mealType || 'dinner',
          family_id: profile.current_family_id,
          user_id: user.id
        });

      if (mealPlanError) {
        console.error('Error adding new recipe to meal plan:', mealPlanError);
        return { success: false, message: mealPlanError.message };
      }

      return { success: true, message: 'Recipe created and added to meal plan!' };
    } catch (error) {
      console.error('Error in acceptNewRecipe:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }
};

export default mealSuggestionService;
